import { useEffect, useRef } from "react";

export const NetworkCanvas = ({ skyline = true, className = "", accent = "212, 175, 94" }) => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let w = 0, h = 0, raf;
    let nodes = [], pulses = [], buildings = [];
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const setup = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.max(12, Math.min(30, Math.floor((w * h) / 42000)));
      nodes = Array.from({ length: count }, (_, i) => ({
        x: Math.random() * w,
        y: Math.random() * h * 0.8,
        vx: (Math.random() - 0.5) * 0.12,
        vy: (Math.random() - 0.5) * 0.09,
        hub: i % 5 === 0,
      }));
      pulses = [];

      buildings = [];
      let x = -30;
      while (x < w + 60) {
        const bw = 36 + Math.random() * 48;
        const bh = 34 + Math.random() * 130;
        const cols = Math.max(2, Math.floor(bw / 12));
        const rows = Math.max(3, Math.floor(bh / 16));
        const lit = Array.from({ length: cols * rows }, () => Math.random() < 0.22);
        buildings.push({ x, w: bw, h: bh, cols, rows, lit });
        x += bw + 8 + Math.random() * 20;
      }
    };

    const drawSkyline = () => {
      for (const b of buildings) {
        ctx.fillStyle = "rgba(5, 12, 24, 0.9)";
        ctx.fillRect(b.x, h - b.h, b.w, b.h);
        const cw = b.w / b.cols;
        const ch = b.h / b.rows;
        for (let r = 0; r < b.rows; r++) {
          for (let c = 0; c < b.cols; c++) {
            const on = b.lit[r * b.cols + c];
            if (Math.random() < 0.0006) b.lit[r * b.cols + c] = !on;
            ctx.fillStyle = on ? `rgba(${accent}, 0.5)` : "rgba(140, 165, 195, 0.06)";
            ctx.fillRect(b.x + c * cw + cw * 0.28, h - b.h + r * ch + ch * 0.3, cw * 0.42, ch * 0.36);
          }
        }
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      if (skyline) drawSkyline();

      const linkDist = Math.min(170, w / 6);
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < linkDist) {
            const alpha = (1 - d / linkDist) * 0.16;
            ctx.strokeStyle = `rgba(148, 178, 214, ${alpha})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      if (!reduced && pulses.length < 7 && Math.random() < 0.025 && nodes.length > 1) {
        const i = Math.floor(Math.random() * nodes.length);
        let j = Math.floor(Math.random() * nodes.length);
        if (j !== i) pulses.push({ a: i, b: j, t: 0, s: 0.0022 + Math.random() * 0.002 });
      }
      pulses = pulses.filter((p) => p.t <= 1);
      for (const p of pulses) {
        const a = nodes[p.a], b = nodes[p.b];
        const x = a.x + (b.x - a.x) * p.t;
        const y = a.y + (b.y - a.y) * p.t;
        p.t += p.s;
        ctx.save();
        ctx.shadowColor = `rgba(${accent}, 0.9)`;
        ctx.shadowBlur = 8;
        ctx.fillStyle = `rgba(${accent}, 0.95)`;
        ctx.beginPath();
        ctx.arc(x, y, 1.7, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      for (const n of nodes) {
        if (n.hub) {
          ctx.strokeStyle = `rgba(${accent}, 0.28)`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(n.x, n.y, 6.5, 0, Math.PI * 2);
          ctx.stroke();
          ctx.fillStyle = `rgba(${accent}, 0.95)`;
          ctx.beginPath();
          ctx.arc(n.x, n.y, 2.2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillStyle = "rgba(172, 198, 228, 0.65)";
          ctx.beginPath();
          ctx.arc(n.x, n.y, 1.3, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    };

    const tick = () => {
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < -20) n.x = w + 20;
        if (n.x > w + 20) n.x = -20;
        if (n.y < -20) n.y = h * 0.8;
        if (n.y > h * 0.82) n.y = -10;
      }
      draw();
      raf = requestAnimationFrame(tick);
    };

    setup();
    if (reduced) draw();
    else raf = requestAnimationFrame(tick);

    const onResize = () => { setup(); if (reduced) draw(); };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [skyline, accent]);

  return <canvas ref={ref} className={className} aria-hidden="true" />;
};
